import { withFinancialsValidation } from "features/new-actor/form/validation-rules/optim/legalEntityValidationRules";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormikProps } from "formik";
import { useParams } from "react-router-dom";
import { useEnhancedFormConfig } from "features/new-actor/hooks/useEnhancedFormConfig";
import { NaturalPersonFormValues } from "features/new-actor/types/naturalPersonFormValues";
import { ActorType } from "features/new-actor/enums/actorType";
import { legalEntityFormConfig } from "features/new-actor/form/optim/legalEntityFormConfig";
import { ActorLayoutVariant } from "features/new-actor/enums/actorLayoutVariant";
import { ActorFormConfig } from "features/new-actor/types/actorFormConfig";
import { BranchCode } from "api/risk";
import { PersonDto } from "api/contract";
import { SearchPersonResult } from "components/domain/SearchBar/types";

export const useActorRegistration = (branchCode: BranchCode) => {
  // fetchSubscriptionProcess is called by SubscriptionProcessWrapper - no need to call it here
  const { riskId } = useParams<{ riskId: string }>();
  const [selectedPerson, setSelectedPerson] = useState<PersonDto>()
  const formikRef = useRef<FormikProps<NaturalPersonFormValues>>(null);

  useEffect(() => {
    if (riskId && branchCode) {
      setSelectedPerson(undefined)
    }
  }, [riskId, branchCode])

  const { naturalPersonFormConfig } = useEnhancedFormConfig();

  // State
  const [isFormValid, setIsFormValid] = useState(false);

  // Computed values
  // Legal protection registers a company only, with its accounting data (required). Its
  // subsidiaries are saved by their own section of the step page, not by this form.
  const formConfig = useMemo<ActorFormConfig>(() => {
    if (branchCode === BranchCode.LegalProtection) {
      return {
        [ActorType.Legal]: withFinancialsValidation(legalEntityFormConfig)
      }
    }
    return {
      [ActorType.Natural]: naturalPersonFormConfig,
      [ActorType.Legal]: legalEntityFormConfig
    }
  }, [branchCode, naturalPersonFormConfig]);

  const handleSelectPerson = useCallback(
    async (person: SearchPersonResult | null) => {
      if (!person) return;
      setSelectedPerson(person.data as PersonDto)
    },
    []
  );

  const handleValidationChange = useCallback((isValid: boolean) => {
    setIsFormValid(isValid);
  }, []);

  const searchBarConfig = useMemo(() => {
    if (branchCode === BranchCode.Unemployment) {
      return {
        withNaturalPerson: true,
        withLegalEntity: false
      }
    }
    if (branchCode === BranchCode.LegalProtection) {
      return {
        withNaturalPerson: false,
        withLegalEntity: true
      }
    }
    return {
      withNaturalPerson: true,
      withLegalEntity: true,
    }
  }, [branchCode])

  const currentLayout = useMemo(() => {
    if (branchCode === BranchCode.Unemployment) {
      return ActorLayoutVariant.UnemploymentActor
    }
    if (branchCode === BranchCode.LegalProtection) {
      return ActorLayoutVariant.LegalProtectionActor
    }

    return ActorLayoutVariant.UpiActor
  }, [branchCode])

  return {
    // State
    isFormValid,

    // Data
    formConfig,

    // Refs
    formikRef,

    // Handlers
    handleValidationChange,
    handleSelectPerson,

    searchBarConfig,
    currentLayout,
    riskId,
    setSelectedPerson,
    selectedPerson
  };
};
